import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';

import { getCatalogItemsAreLoading } from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { fetchCatalogItems } from 'entities/CatalogItem/model/services/fetchCatalogItems/fetchCatalogItems';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { CatalogItemsRequestData } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import {
    getSearchCatalogItemIsActive,
    getSearchLocalCatalogItemValue,
} from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import { searchCatalogItemsActions } from 'features/SearchCatalogItems/model/slices/SearchCatalogItemsSlice';
import {
    getRouteOrdersAddItemsCategories,
    getRouteOrdersAddItemsInventory,
} from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useDebouncedCallback } from 'shared/lib/hooks/useDebouncedCallback';
import { useFetchWIthController } from 'shared/lib/hooks/useFetchWIthController';
import { SearchField } from 'shared/ui/SearchField/SearchField';

interface SearchCatalogItemsFieldProps {
    catalogId: string;
    hideBorder: boolean;
    isInventory?: boolean;
}

export const SearchCatalogItemsField = memo(
    ({
        catalogId,
        hideBorder,
        isInventory = false,
    }: SearchCatalogItemsFieldProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const fetchWithController = useFetchWIthController();

        const isCategories = useMatch(getRouteOrdersAddItemsCategories());
        const isInventoryRoute = useMatch(getRouteOrdersAddItemsInventory());

        const isActive = useSelector(getSearchCatalogItemIsActive);
        const isLoading = useSelector(getCatalogItemsAreLoading);
        const search = useSelector(getSearchLocalCatalogItemValue);

        const searchPlaceholder = useMemo(() => {
            if (isCategories) return t('Search property catalog');
            if (isInventoryRoute) return t('Search property inventory');
            return `${t('Search by name')}, ${t('model, SKU, or MPN')}`;
        }, [isCategories, isInventoryRoute, t]);

        const searchItemsDebounced = useDebouncedCallback(
            async (params: CatalogItemsRequestData) => {
                await dispatch(fetchCatalogItems(params));
            },
        );

        const onSetValue = async (searchStr: string) => {
            if (!searchStr && isActive) {
                dispatch(searchCatalogItemsActions.setActive(false));
            } else if (searchStr && !isActive) {
                dispatch(searchCatalogItemsActions.setActive(true));
            }

            if (!isLoading && searchStr) {
                dispatch(catalogItemActions.setLoading(true));
            }

            dispatch(
                searchCatalogItemsActions.setLocalCatItemSearchValue(searchStr),
            );

            if (searchStr) {
                const data = {
                    catalogId,
                    searchStr,
                    isInventory,
                };

                fetchWithController((controller: AbortController) =>
                    searchItemsDebounced({
                        data,
                        abortController: controller,
                    }),
                );
            }
        };

        return (
            <SearchField
                hideBorder={hideBorder}
                placeholder={searchPlaceholder}
                value={search}
                setValue={onSetValue}
                resettable={true}
                isActive={isActive}
            />
        );
    },
);
